import React, { lazy, Suspense, useEffect } from 'react';
import { Navigate, useLocation, useRoutes } from 'react-router-dom';
// layouts
import DashboardLayout from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
// guards
import GuestGuard from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';
import { PATH_AFTER_LOGIN } from '../config';
import LoadingScreen from '../components/LoadingScreen';
import LocationManagement from '../pages/dashboard/settings/location';
import UserManagementAccount from '../pages/dashboard/hr/user/userAccount';
import PaySheetIndex from '../pages/dashboard/hr/paysheet/paySheetIndex';
import FoodList from '../pages/dashboard/food/foodList';
import FoodCategoryManagement from '../pages/dashboard/food/foodCategory';
import { dispatch } from '../redux/store';
import { getLocations } from '../redux/slices/settingsManagementRedux/locationsRedux';
import FoodEditor from '../pages/dashboard/food/foodEditor';
import POS from '../pages/dashboard/pos/POS';
import { getPermissions } from '../redux/slices/commonRedux/commonRedux';
import HomeDashboard from '../pages/dashboard/home/homeDashboard';
import { accessVerify } from '../utils/common';
import PermissionRequired from '../pages/errorPages/permissionRequired';
import MetaDashboard from '../pages/dashboard/home/metaDashboard';
import ProductTags from '../pages/dashboard/product/productTags';
import ProductCategories from '../pages/dashboard/product/productCategories';
import ProductAttributes from '../pages/dashboard/product/productAttributes';
import ProductList from '../pages/dashboard/product/productList';
import ProductEditor from '../pages/dashboard/product/productEditor/productEditor'; ;
import Guest from '../pages/dashboard/guest/guest';
import GuestCards from '../pages/dashboard/guest/guestCards';
import Billing from '../pages/dashboard/billing/billing';
import BillingSecondView from '../pages/dashboard/billing/billingSeondView';
import GuestRevenueReport from '../pages/dashboard/guest/guestRevenueReport';
import FinanceDashboard from '../pages/dashboard/home/financeDashboard';
import SalesMenuDashboard from '../pages/dashboard/home/salesMenuDashboard';
// ----------------------------------------------------------------------

const Loadable = (Component) => (props) => {
  const { pathname } = useLocation();

  return (
    <Suspense fallback={<LoadingScreen isDashboard={pathname.includes('/dashboard')} />}>
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  useEffect(() => {
    dispatch(getPermissions());
    dispatch(getLocations());
    // dispatch(getUnits());
  }, []);

  return useRoutes([
    {
      path: 'pay',
      children: [
        {
          path: 'sheet/:token',
          element: <PaySheetIndex />,
        },
      ],
    },
    {
      path: 'secondDisplay',
      element: <BillingSecondView />,
    },
    {
      path: 'auth',
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
        {
          path: 'register',
          element: (
            <GuestGuard>
              <Register />
            </GuestGuard>
          ),
        },
        { path: 'login-unprotected', element: <Login /> },
        { path: 'register-unprotected', element: <Register /> },
        { path: 'reset-password', element: <ResetPassword /> },
        { path: 'verify', element: <VerifyCode /> },
      ],
    },
    {
      path: 'dashboard',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to={PATH_AFTER_LOGIN} replace />, index: true },
        { path: 'home', element: <HomeDashboard /> },
        { path: 'app', element: <GeneralApp /> },
        { path: 'analytics', element: <GeneralAnalytics /> },
        { path: 'banking', element: <GeneralBanking /> },
        { path: 'booking', element: <GeneralEcommerce /> },
        { path: 'locations', element: <LocationManagement /> },
        { path: 'pos', element: <POS /> },
        {
          path: 'products',
          children: [
            { path: 'list', element: <ProductList /> },
            { path: 'editor/:productId/:parentId', element: <ProductEditor /> },
            { path: 'category', element: <ProductCategories /> },
            { path: 'tags', element: <ProductTags /> },
            { path: 'attributes', element: <ProductAttributes /> },
            // { path: 'variations', element: <ProductVariations /> },
            // { path: 'manage/:productId', element: <ProductEditor /> },
          ],
        },
        {
          path: 'report',
          children: [
            { path: 'home', element: accessVerify('DASHBOARD_ANALYTICS_PAGE') ? <HomeDashboard /> : <PermissionRequired /> },
            { path: 'finance', element: accessVerify('DASHBOARD_ANALYTICS_PAGE') ? <FinanceDashboard /> : <PermissionRequired /> },
            { path: 'sales-menu', element: accessVerify('DASHBOARD_ANALYTICS_PAGE') ? <SalesMenuDashboard /> : <PermissionRequired /> },
          ],
        },
        { path: 'home', element: accessVerify('DASHBOARD_ANALYTICS_PAGE') ? <HomeDashboard /> : <PermissionRequired /> },
        { path: 'finance', element: accessVerify('DASHBOARD_ANALYTICS_PAGE') ? <FinanceDashboard /> : <PermissionRequired /> },
        { path: 'meta/:type', element: accessVerify('DASHBOARD_META_DASHBOARD') ? <MetaDashboard /> : <PermissionRequired /> },
        {
          path: 'foods',
          children: [
            { path: 'category/:type', element: accessVerify('FOOD_CATEGORY_PAGE') ? <FoodCategoryManagement /> : <PermissionRequired /> },
            { path: 'types/:paramType', element: accessVerify('FOOD_PAGE') ? <FoodList /> : <PermissionRequired /> },
            { path: 'food/:type/manage/:foodId', element: accessVerify('FOOD_UPDATE') ? <FoodEditor /> : <PermissionRequired /> },
          ],
        },
        { path: 'billing', element: accessVerify('PAYMENTS') ? <Billing /> : <PermissionRequired /> },
        { path: 'billing/:id', element: accessVerify('PAYMENTS') ? <Billing /> : <PermissionRequired /> },
        {
          path: 'guest',
          children: [
            { path: 'list', element: accessVerify('GUEST_LIST') ? <Guest /> : <PermissionRequired /> },
            { path: 'card', element: accessVerify('GUEST_CARD') ? <GuestCards /> : <PermissionRequired /> },
            { path: 'revenueReport', element: accessVerify('GUEST_LIST') ? <GuestRevenueReport /> : <PermissionRequired /> },
          ],
        },
        {
          path: 'hr',
          children: [
            {
              path: 'employees',
              children: [
                { path: 'list/:type', element: accessVerify('USER_LIST_PAGE') ? <UsersList /> : <PermissionRequired /> },
                { path: 'account/:id', element: accessVerify('USER_PROFILE_VIEW') ? <UserManagementAccount /> : <PermissionRequired /> },
              ],
            },
          ],
        },
        {
          path: 'settings',
          children: [
            {
              path: 'general',
              children: [{ path: 'locations', element: accessVerify('BUSINESS_LOCATIONS_PAGE') ? <LocationManagement /> : <PermissionRequired /> }],
            },
            {
              path: 'account',
              children: [{ path: 'userAccount/:id', element: <UserManagementAccount /> }],
            },
          ],
        },
      ],
    },

    // Main Routes
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: 'coming-soon', element: <ComingSoon /> },
        { path: 'maintenance', element: <Maintenance /> },
        { path: 'pricing', element: <Pricing /> },
        { path: 'payment', element: <Payment /> },
        { path: '500', element: <Page500 /> },
        { path: '404', element: <NotFound /> },
        { path: '*', element: <Navigate to='/404' replace /> },
      ],
    },
    {
      path: '/',
      element: (
        <GuestGuard>
          <Login />
        </GuestGuard>
      ),
    },
    { path: '*', element: <Navigate to='/404' replace /> },
  ]);
}

// AUTHENTICATION
const Login = Loadable(lazy(() => import('../pages/auth/Login')));
const Register = Loadable(lazy(() => import('../pages/auth/Register')));
const ResetPassword = Loadable(lazy(() => import('../pages/auth/ResetPassword')));
const VerifyCode = Loadable(lazy(() => import('../pages/auth/VerifyCode')));

// DASHBOARD

// GENERAL
const GeneralApp = Loadable(lazy(() => import('../pages/template/GeneralApp')));
const GeneralEcommerce = Loadable(lazy(() => import('../pages/template/GeneralEcommerce')));
const GeneralAnalytics = Loadable(lazy(() => import('../pages/template/GeneralAnalytics')));
const GeneralBanking = Loadable(lazy(() => import('../pages/template/GeneralBanking')));
const GeneralBooking = Loadable(lazy(() => import('../pages/template/GeneralBooking')));

// ECOMMERCE
const EcommerceShop = Loadable(lazy(() => import('../pages/template/EcommerceShop')));
const EcommerceProductDetails = Loadable(lazy(() => import('../pages/template/EcommerceProductDetails')));
const EcommerceProductList = Loadable(lazy(() => import('../pages/template/EcommerceProductList')));
const EcommerceProductCreate = Loadable(lazy(() => import('../pages/template/EcommerceProductCreate')));
const EcommerceCheckout = Loadable(lazy(() => import('../pages/template/EcommerceCheckout')));

// INVOICE
const InvoiceList = Loadable(lazy(() => import('../pages/template/InvoiceList')));
const InvoiceDetails = Loadable(lazy(() => import('../pages/template/InvoiceDetails')));
const InvoiceCreate = Loadable(lazy(() => import('../pages/template/InvoiceCreate')));
const InvoiceEdit = Loadable(lazy(() => import('../pages/template/InvoiceEdit')));

// BLOG
const BlogPosts = Loadable(lazy(() => import('../pages/template/BlogPosts')));
const BlogPost = Loadable(lazy(() => import('../pages/template/BlogPost')));
const BlogNewPost = Loadable(lazy(() => import('../pages/template/BlogNewPost')));

// USER
const UserProfile = Loadable(lazy(() => import('../pages/template/UserProfile')));
const UserCards = Loadable(lazy(() => import('../pages/template/UserCards')));
const UserList = Loadable(lazy(() => import('../pages/template/UserList')));
const UsersList = Loadable(lazy(() => import('../pages/dashboard/hr/user/userList')));
const UserAccount = Loadable(lazy(() => import('../pages/template/UserAccount')));
const UserCreate = Loadable(lazy(() => import('../pages/template/UserCreate')));

const Mail = Loadable(lazy(() => import('../pages/template/Mail')));
const Calendar = Loadable(lazy(() => import('../pages/template/Calendar')));
const Kanban = Loadable(lazy(() => import('../pages/template/Kanban')));

// MAIN
const ComingSoon = Loadable(lazy(() => import('../pages/template/ComingSoon')));
const Maintenance = Loadable(lazy(() => import('../pages/errorPages/maintenance')));
const Pricing = Loadable(lazy(() => import('../pages/template/Pricing')));
const Payment = Loadable(lazy(() => import('../pages/template/Payment')));
const Page500 = Loadable(lazy(() => import('../pages/errorPages/page500')));
const NotFound = Loadable(lazy(() => import('../pages/errorPages/page404')));
